import React from 'react';
import { Layers } from 'lucide-react';

export const LogicalLevels: React.FC = () => {
  const levels = [
    { 
      id: 1, 
      title: 'Spirituality', 
      description: 'Connection to something greater than yourself',
      color: 'primary',
      questions: ['What is my purpose?', 'What am I here to do?', 'What gives my life meaning?']
    },
    { 
      id: 2, 
      title: 'Identity', 
      description: 'Who you are as a person',
      color: 'primary',
      questions: ['Who am I?', 'What kind of person am I?', 'What is my role?']
    },
    { 
      id: 3, 
      title: 'Values & Beliefs', 
      description: 'What you consider important and true',
      color: 'secondary',
      questions: ['What do I value most?', 'What do I believe in?', 'What principles guide me?']
    },
    { 
      id: 4, 
      title: 'Skills & Capabilities', 
      description: 'What you can do and how well',
      color: 'secondary',
      questions: ['What am I good at?', 'What skills do I have?', 'What can I learn?']
    },
    { 
      id: 5, 
      title: 'Behavior', 
      description: 'What you do and how you act',
      color: 'accent',
      questions: ['What do I do?', 'How do I behave?', 'What actions do I take?']
    },
    { 
      id: 6, 
      title: 'Environment', 
      description: 'Your physical and social context',
      color: 'accent',
      questions: ['Where am I?', 'What is my context?', 'What surrounds me?']
    }
  ];

  const getColorClasses = () => {
    return 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-pri)]';
  };

  return (
    <div className="min-h-screen bg-[var(--surface)] py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Layers className="h-12 w-12 text-consulting-slate-blue-600" />
            <h1 className="text-4xl font-bold text-charcoal-900">Logical Levels of Change</h1>
          </div>
          <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
            A hierarchical framework for understanding change at different levels, 
            from environment to spirituality, helping you identify where real change needs to happen.
          </p>
        </div>

        {/* Important Tip */}
        <div className="mb-8 p-6 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <p className="text-[var(--text-pri)] text-lg font-medium">
            "Way to tell a story - who you are, what you value, what you're good at"
          </p>
          <p className="text-[var(--text-sec)] mt-2">
            Each level tells a different part of your story, and change at higher levels 
            has more impact than change at lower levels.
          </p>
        </div>

        {/* Main Levels */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">The Six Levels</h2>
          
          <div className="space-y-4">
            {levels.map((level, index) => (
              <div key={level.id} className="relative">
                {/* Connection Line */}
                {index < levels.length - 1 && (
                  <div className="absolute left-8 top-20 w-0.5 h-8 bg-[var(--rule-bright)] z-0"></div>
                )}
                
                {/* Level */}
                <div className={`p-6  border-2 ${getColorClasses(level.color)} relative z-10`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--bg-raised)] border-2 border-current flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {level.id}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{level.title}</h3>
                      <p className="text-sm opacity-80 mb-3">{level.description}</p>
                      <div>
                        <h4 className="font-semibold mb-2">Key Questions:</h4>
                        <ul className="text-sm space-y-1">
                          {level.questions.map((question, qIndex) => (
                            <li key={qIndex}>• {question}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Change Principles */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">How Change Works</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-charcoal-800 mb-3">Change Flows Down:</h3>
              <ul className="space-y-2 text-charcoal-600">
                <li>• Change at higher levels affects lower levels</li>
                <li>• Identity change affects behavior and environment</li>
                <li>• Values change affects skills and actions</li>
                <li>• Beliefs change affects capabilities and behavior</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-charcoal-800 mb-3">Diagnostic Questions:</h3>
              <ul className="space-y-2 text-charcoal-600">
                <li>• "At what level is the real issue?"</li>
                <li>• "What level of change is needed?"</li>
                <li>• "Where should I focus my energy?"</li>
                <li>• "What story am I telling myself?"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Usage Examples */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">When to Use This Tool</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Personal Development</h3>
              <p className="text-[var(--text-sec)] text-sm">Understand where you need to focus for meaningful change in your life.</p>
            </div>
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Coaching</h3>
              <p className="text-[var(--text-sec)] text-sm">Help clients identify the right level for change and tell their story effectively.</p>
            </div>
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Leadership</h3>
              <p className="text-[var(--text-sec)] text-sm">Understand how to create change at different levels in your organization.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
